<template>
  <Card cardClasses="shadow-sm border-0">
    <div class="d-flex justify-content-between align-items-center mb-4">
      <h4 class="m-0 text-primary">
        <i class="bi bi-phone me-2"></i>M-Pesa Integrations
      </h4>
      <div class="d-flex gap-3">
        <button class="btn btn-outline-secondary" @click="$router.go(-1)">
          <i class="bi bi-arrow-left me-1"></i> Back
        </button>
        <button class="btn btn-primary" @click="openModal('add')">
          <i class="bi bi-plus-circle me-1"></i> Add Integration
        </button>
      </div>
    </div>

    <div class="container-fluid p-0">
      <div v-if="loading" class="text-center py-5">
        <Spinner spinner-class="mb-1" />
        <p class="text-muted mt-2">Loading integrations...</p>
      </div>

      <div v-if="!loading" class="integrations-content fade-in">
        <div class="d-flex flex-wrap align-items-center justify-content-between mb-3">
          <div class="d-flex align-items-center mb-2 mb-sm-0">
            <span class="badge bg-primary rounded-pill me-2">{{ filteredIntegrations.length }}</span>
            <span class="text-muted">Integrations</span>
          </div>
          <UInput v-model="dataTableSearch" size="md" placeholder="Search integrations" class="mb-0 w-auto" />
        </div>

        <div class="card border-0 shadow-sm mb-4">
          <div class="table-responsive">
            <table class="table table-hover align-middle mb-0">
              <thead>
              <tr>
                <th>STORE</th>
                <th>NAME</th>
                <th>ACCOUNT TYPE</th>
                <th>PARENT CODE</th>
                <th>SHORT CODE</th>
                <th>TILL NUMBER</th>
                <th>STATUS</th>
                <th>CREATED</th>
                <th class="text-end">ACTIONS</th>
              </tr>
              </thead>
              <tbody>
              <tr v-for="(integration, index) in paginatedIntegrations" :key="integration.id" class="integration-row" :class="{'bg-light': index % 2 === 0}">
                <td>
                  <div class="d-flex align-items-center">
                    <div class="integration-avatar me-2 bg-primary-light">
                      <i class="bi bi-shop"></i>
                    </div>
                    <span class="fw-medium">{{ getStoreName(integration.store_id) }}</span>
                  </div>
                </td>
                <td>
                  <span class="fw-medium">{{ integration.name || 'N/A' }}</span>
                </td>
                <td>
                  <span class="badge" :class="integration.type === 'Paybill' ? 'bg-info' : 'bg-warning'">
                    {{ integration.type || 'N/A' }}
                  </span>
                </td>
                <td>
                  <span class="fw-medium">{{ integration.parent_code }}</span>
                </td>
                <td>
                  <span class="fw-medium">{{ integration.short_code }}</span>
                </td>
                <td>
                  <span class="fw-medium">{{ integration.till_number }}</span>
                </td>
                <td>
                  <span class="badge" :class="integration.status === 'active' ? 'bg-success' : 'bg-danger'">
                    <i class="bi" :class="integration.status === 'active' ? 'bi-check-circle' : 'bi-x-circle'"></i>
                    {{ integration.status === 'active' ? 'Active' : 'Inactive' }}
                  </span>
                </td>
                <td>{{ formatDate(integration.created_at) }}</td>
                <td class="text-end">
                  <button class="btn btn-sm btn-outline-success me-2" @click="openRegisterUrlModal(integration)">
                    <i class="bi bi-link-45deg"></i> Register URL
                  </button>
                  <button class="btn btn-sm btn-outline-primary me-2" @click="openModal('edit', integration)">
                    <i class="bi bi-pencil"></i> Edit
                  </button>
                </td>
              </tr>
              <tr v-if="paginatedIntegrations.length === 0">
                <td colspan="8" class="text-center py-4 text-muted">
                  <i class="bi bi-inbox fs-1 d-block mb-2"></i>
                  No M-Pesa integrations found
                </td>
              </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div class="d-flex flex-wrap pt-3 pb-4 align-items-end mx-4 justify-content-between">
          <div class="me-sm-4 mb-sm-0 mb-3">
            <p class="mb-2 small">Rows per page</p>
            <select class="form-select" v-model.number="dataTablePerPage">
              <option v-for="item in [5,10,25,50,100]" :key="item" :value="item">{{item}}</option>
            </select>
          </div>
          <div>
            <Pagination
              v-model="dataTablePage"
              :pages="totalPages"
              :threshold="2">
              <template #previous>Prev</template>
              <template #next>Next</template>
            </Pagination>
          </div>
        </div>

        <div class="d-flex justify-content-center mt-4">
          <button class="btn btn-outline-secondary" @click="$router.go(-1)">
            <i class="bi bi-arrow-left me-1"></i> Back
          </button>
        </div>
      </div>
    </div>

    <Modal :open="integrationModal" @closed="integrationModal = false" dialog-size="modal-lg">
      <div class="modal-header bg-light">
        <h5 class="modal-title m-0">
          <i class="bi" :class="integrationMode === 'add' ? 'bi-plus-circle' : 'bi-pencil-square'"></i>
          {{ integrationMode === 'add' ? 'Add M-Pesa Integration' : 'Edit M-Pesa Integration' }}
        </h5>
        <button @click="integrationModal = false" type="button" class="btn-close outline-none" data-dismiss="modal" aria-label="Close">
        </button>
      </div>
      <div class="modal-body">
        <form @submit.prevent="">
          <div class="row g-3">
            <div class="form-group col-md-6">
              <label for="store" class="form-label">
                Store <span class="text-danger">*</span>
              </label>
              <select
                  id="store"
                  class="form-select"
                  v-model="integration.store_id"
                  :disabled="integrationMode === 'edit'"
              >
                <option value="">Select Store</option>
                <option v-for="store in stores" :key="store.id" :value="store.id">
                  {{ store.name }}
                </option>
              </select>
            </div>
            <div class="form-group col-md-6">
              <label for="name" class="form-label">
                Name <span class="text-danger">*</span>
              </label>
              <input
                  type="text"
                  id="name"
                  class="form-control"
                  placeholder="e.g., Main Store Paybill"
                  v-model="integration.name"
                  maxlength="100"
              >
              <small class="text-muted">Integration name for identification</small>
            </div>
            <div class="form-group col-md-6">
              <label for="type" class="form-label">
                Account Type <span class="text-danger">*</span>
              </label>
              <select
                  id="type"
                  class="form-select"
                  v-model="integration.type"
              >
                <option value="">Select Account Type</option>
                <option value="Paybill">Paybill</option>
                <option value="Till">Till</option>
              </select>
              <small class="text-muted">Select Paybill or Till</small>
            </div>
            <div class="form-group col-md-6">
              <label for="status" class="form-label">
                Status
              </label>
              <select
                  id="status"
                  class="form-select"
                  v-model="integration.status"
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
            <div class="form-group col-md-4">
              <label for="parent_code" class="form-label">
                Parent Code <span class="text-danger">*</span>
              </label>
              <input
                  type="text"
                  id="parent_code"
                  class="form-control"
                  placeholder="e.g., 174379"
                  v-model="integration.parent_code"
                  maxlength="20"
              >
              <small class="text-muted">M-Pesa Business Short Code</small>
            </div>
            <div class="form-group col-md-4">
              <label for="short_code" class="form-label">
                Store Number <span class="text-danger">*</span>
              </label>
              <input
                  type="text"
                  id="short_code"
                  class="form-control"
                  placeholder="e.g., 600123"
                  v-model="integration.short_code"
                  maxlength="20"
              >
              <small class="text-muted">M-Pesa Store Number</small>
            </div>
            <div class="form-group col-md-4">
              <label for="short_code" class="form-label">
                Till Number <span class="text-danger">*</span>
              </label>
              <input
                  type="text"
                  id="short_code"
                  class="form-control"
                  placeholder="e.g., 5632541"
                  v-model="integration.till_number"
                  maxlength="20"
              >
              <small class="text-muted">M-Pesa Till Number (PartyB)</small>
            </div>
            <div class="form-group col-md-12">
              <label for="consumer_key" class="form-label">
                Consumer Key <span class="text-danger">*</span>
              </label>
              <input
                  type="text"
                  id="consumer_key"
                  class="form-control"
                  placeholder="Enter M-Pesa Consumer Key"
                  v-model="integration.consumer_key"
              >
            </div>
            <div class="form-group col-md-12">
              <label for="consumer_secret" class="form-label">
                Consumer Secret <span class="text-danger">*</span>
              </label>
              <input
                  type="text"
                  id="consumer_secret"
                  class="form-control"
                  placeholder="Enter M-Pesa Consumer Secret"
                  v-model="integration.consumer_secret"
              >
            </div>
            <div class="form-group col-md-12">
              <label for="pass_key" class="form-label">
                Passkey <span class="text-danger">*</span>
              </label>
              <input
                  type="text"
                  id="pass_key"
                  class="form-control"
                  placeholder="Enter M-Pesa Passkey"
                  v-model="integration.pass_key"
              >
            </div>
          </div>
        </form>
      </div>
      <div class="modal-footer bg-light">
        <button type="button" class="btn btn-outline-secondary" @click="integrationModal = false">
          <i class="bi bi-x-circle me-1"></i>Cancel
        </button>
        <button
            type="button"
            class="btn btn-primary"
            v-if="integrationMode === 'add'"
            @click="addIntegration"
        >
          <i class="bi bi-plus-circle me-1"></i>Create Integration
        </button>
        <button
            type="button"
            class="btn btn-primary"
            v-else
            @click="updateIntegration"
        >
          <i class="bi bi-check-circle me-1"></i>Update Integration
        </button>
      </div>
    </Modal>

    <Modal :open="registerUrlModal" @closed="registerUrlModal = false" dialog-size="modal-md">
      <div class="modal-header bg-success text-white">
        <h5 class="modal-title m-0">
          <i class="bi bi-link-45deg me-2"></i>Register C2B URL
        </h5>
        <button @click="registerUrlModal = false" type="button" class="btn-close btn-close-white" data-dismiss="modal" aria-label="Close">
        </button>
      </div>
      <div class="modal-body">
        <div class="alert alert-info">
          <i class="bi bi-info-circle me-2"></i>
          This will register the confirmation URL with M-Pesa for C2B transactions.
        </div>
        <form @submit.prevent="">
          <div class="form-group mb-3">
            <label for="register_store" class="form-label fw-bold">
              Store
            </label>
            <input
                type="text"
                id="register_store"
                class="form-control"
                :value="getStoreName(registerUrlData.store_id)"
                disabled
            >
          </div>
          <div class="form-group mb-3">
            <label for="register_short_code" class="form-label fw-bold">
              Short Code
            </label>
            <input
                type="text"
                id="register_short_code"
                class="form-control"
                v-model="registerUrlData.short_code"
                disabled
            >
          </div>
        </form>
      </div>
      <div class="modal-footer">
        <button type="button" class="btn btn-outline-secondary" @click="registerUrlModal = false">
          <i class="bi bi-x-circle me-1"></i> Cancel
        </button>
        <button type="button" class="btn btn-success" @click="registerC2BUrl">
          <i class="bi bi-check-circle me-1"></i> Register URL
        </button>
      </div>
    </Modal>
  </Card>
</template>

<script>
import storeMpesaService from "@/api/storeMpesaService";
import storeService from "@/api/storeService";
import formatDate from "@/utils/formatDate";
import {decrypt} from "@/utils/crypto";
import Pagination from "@/components/pagination/Pagination.vue";

export default {
  name: "MpesaIntegrations",

  components: {
    Pagination
  },

  data(){
    this.organization = JSON.parse(decrypt(sessionStorage.getItem('organization')))
    return {
      organization: {},
      loading: true,
      integrations: [],
      stores: [],
      integrationMode: 'add',
      integrationModal: false,
      dataTableSearch: '',
      dataTablePerPage: 10,
      dataTablePage: 1,
      registerUrlModal: false,
      registerUrlData: {
        store_id: '',
        short_code: ''
      },
      integration: {
        organization_id: this.organization.id,
        store_id: '',
        name: '',
        type: '',
        parent_code: '',
        short_code: '',
        till_number: '',
        consumer_key: '',
        consumer_secret: '',
        pass_key: '',
        status: 'inactive'
      },
      currentIntegration: null
    }
  },

  methods: {
    formatDate,

    async getIntegrations() {
      this.loading = true;
      try {
        const response = await storeMpesaService.getMpesaIntegrations();
        this.integrations = response || [];
      } catch (error) {
        console.error("Error fetching integrations:", error);
        this.integrations = [];
      } finally {
        this.loading = false;
      }
    },

    async getStores() {
      try {
        this.stores = await storeService.getStores();
      } catch (error) {
        console.error("Error fetching stores:", error);
      }
    },

    getStoreName(storeId) {
      const store = this.stores.find(s => s.id === storeId);
      return store ? store.name : 'Unknown Store';
    },

    openModal(mode, integration = null) {
      this.integrationMode = mode;
      if (mode === 'add') {
        this.resetForm();
      } else {
        this.currentIntegration = integration;
        this.integration = {
          organization_id: this.organization.id,
          store_id: integration.store_id,
          name: integration.name,
          type: integration.type,
          parent_code: integration.parent_code,
          short_code: integration.short_code,
          till_number: integration.till_number,
          consumer_key: integration.consumer_key,
          consumer_secret: '',
          pass_key: '',
          status: integration.status
        };
      }
      this.integrationModal = true;
    },

    async addIntegration() {
      if (!this.integration.type || !['Paybill', 'Till'].includes(this.integration.type)) {
        notify.alert.error('Please select a valid Account Type (Paybill or Till)');
        return;
      }
      try {
        const response = await storeMpesaService.createMpesaIntegration(this.integration);
        if (response) {
          this.integrationModal = false;
          await this.getIntegrations();
          this.resetForm();
        }
      } catch (error) {
        console.error("Error adding integration:", error);
      }
    },

    async updateIntegration() {
      if (!this.integration.type || !['Paybill', 'Till'].includes(this.integration.type)) {
        notify.alert.error('Please select a valid Account Type (Paybill or Till)');
        return;
      }
      try {
        const updateData = {
          name: this.integration.name,
          type: this.integration.type,
          parent_code: this.integration.parent_code,
          short_code: this.integration.short_code,
          till_number: this.integration.till_number,
          consumer_key: this.integration.consumer_key,
          status: this.integration.status
        };

        if (this.integration.consumer_secret) {
          updateData.consumer_secret = this.integration.consumer_secret;
        }

        if (this.integration.pass_key) {
          updateData.pass_key = this.integration.pass_key;
        }

        const response = await storeMpesaService.updateMpesaIntegration(updateData, this.currentIntegration.id);
        if (response) {
          this.integrationModal = false;
          await this.getIntegrations();
          this.resetForm();
        }
      } catch (error) {
        console.error("Error updating integration:", error);
      }
    },

    resetForm() {
      this.integration = {
        organization_id: this.organization.id,
        store_id: '',
        name: '',
        type: '',
        parent_code: '',
        short_code: '',
        till_number: '',
        consumer_key: '',
        consumer_secret: '',
        pass_key: '',
        status: 'active'
      };
      this.currentIntegration = null;
    },

    openRegisterUrlModal(integration) {
      this.registerUrlData = {
        store_id: integration.store_id,
        short_code: integration.short_code
      };
      this.registerUrlModal = true;
    },

    async registerC2BUrl() {
      try {
        const response = await storeMpesaService.registerC2BUrl(this.registerUrlData);
        if (response) {
          this.registerUrlModal = false;
        }
      } catch (error) {
        console.error("Error registering C2B URL:", error);
      }
    }
  },

  created: async function () {
    try {
      this.organization = JSON.parse(decrypt(sessionStorage.getItem('organization')));
      await this.getStores();
      await this.getIntegrations();
    } catch (error) {
      console.error("Error in created lifecycle hook:", error);
      this.loading = false;
    }
  },

  computed: {
    filteredIntegrations(){
      if (!this.dataTableSearch) return this.integrations;
      const term = this.dataTableSearch.toLowerCase();
      return this.integrations.filter(i =>
          (i.parent_code && i.parent_code.toLowerCase().includes(term)) ||
          (i.short_code && i.short_code.toLowerCase().includes(term)) ||
          (i.status && i.status.toLowerCase().includes(term)) ||
          (this.getStoreName(i.store_id).toLowerCase().includes(term))
      );
    },
    paginatedIntegrations(){
      const start = (this.dataTablePage - 1) * this.dataTablePerPage;
      return this.filteredIntegrations.slice(start, start + this.dataTablePerPage);
    },
    totalPages(){
      return Math.ceil(this.filteredIntegrations.length / this.dataTablePerPage) || 1;
    }
  },

  watch: {
    dataTablePerPage(){
      this.dataTablePage = 1;
    }
  }
}
</script>

<style scoped>
.integrations-content {
  padding: 0.5rem;
}

.table th {
  font-weight: 600;
  text-transform: uppercase;
  font-size: 0.7rem;
  letter-spacing: 0.5px;
  color: #6b7280;
  border-top: none;
  padding: 1rem 1.5rem;
  background-color: #f9fafb;
  border-bottom: 1px solid #e9ecef;
}

.table td {
  padding: 1rem 1.5rem;
  vertical-align: middle;
  border-color: #e9ecef;
  color: #4b5563;
}

.integration-row {
  transition: all 0.2s ease;
}

.integration-row:hover {
  background-color: #f8f9fa;
  transform: translateY(-2px);
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);
}

.integration-avatar {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.2rem;
}

.bg-primary-light {
  background-color: #eef2ff;
  color: #4361ee;
}

.badge {
  font-weight: 500;
  padding: 0.35em 0.65em;
  font-size: 0.75em;
  border-radius: 6px;
}

.fade-in {
  animation: fadeIn 0.3s ease-in-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@media (max-width: 768px) {
  .table th,
  .table td {
    padding: 0.75rem 1rem;
  }

  .integration-avatar {
    width: 32px;
    height: 32px;
    font-size: 1rem;
  }
}
</style>
